import React, { FunctionComponent } from 'react';
import { Public } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import {
  ListChange,
  ListItemChangeDetail,
  ListItemChangeType,
} from '@upstart/harmony/lib/legos/propertyChangeTypes';
import { FinanceType } from '@upstart/harmony/lib/constants';
import { formatName, formatValue } from './propertyFormatter';
import { FINANCE_TYPE_PROPERTIES, GLOBAL_PROPERTIES } from '../Constants';
import { ChangeDescription, ChangeInfo, ChangeType } from './ChangeDescription';
import './PropertyChange.scss';

const EXCLUDE_FROM_EDIT = ['programs'];
const EXCLUDE_FROM_ADD_DELETE = ['faux-trade-in'];
const EXCLUDE_ITEM_PROPERTIES = ['visualIndex'];
const SELECTED_PRODUCTS = 'selectedProducts';

interface ListPropertyProps {
  change: ListChange;
  financeType: FinanceType;
}

/**
 * Sample output would be somthing like:
 * <div>Protection Plans</div>
 * <ul>
 *  <li>Added Key Replacement with value <span className="offer-prop-change-value">$695.00</span></li>
 *  <li>Deleted Theft Deterrent with value <span className="offer-prop-change-value">$1000.00</span></li>
 * </ul>
 *
 * @param param0
 * @returns
 */
export const ListProperty: FunctionComponent<ListPropertyProps> = ({
  change,
  financeType,
}) => {
  const renderDetail = (itemChange: ListItemChangeDetail): JSX.Element => {
    const textChanges = getTextChanges(change.name, itemChange);
    if (textChanges.length === 0) {
      return <li />;
    }
    if (
      change.name === SELECTED_PRODUCTS ||
      itemChange.type === ListItemChangeType.ADD ||
      itemChange.type === ListItemChangeType.DELETE
    ) {
      return (
        <ChangeDescription
          changeType={textChanges[0].changeType}
          name={textChanges[0].name}
          values={textChanges[0].values}
        />
      );
    }
    return (
      <li key={itemChange.name}>
        <div>Edited {formatName(itemChange.name)}</div>
        <ul>
          {textChanges.map(textChange => (
            <ChangeDescription
              changeType={textChange.changeType}
              name={textChange.name}
              values={textChange.values}
            />
          ))}
        </ul>
      </li>
    );
  };

  const isFinance = FINANCE_TYPE_PROPERTIES.includes(change.name);
  const isGlobal = GLOBAL_PROPERTIES.includes(change.name);

  const filteredListItemChanges = change.listItemChanges?.filter(
    itemChange => getTextChanges(change.name, itemChange).length > 0,
  );
  if (!filteredListItemChanges || filteredListItemChanges.length === 0) {
    return <div />;
  }
  return (
    <div>
      {isFinance || isGlobal ? (
        <div className="offer-prop-change-name-container">
          <div>{formatName(change.name)}</div>
          <div>
            <Tooltip
              title={
                <span style={{ fontSize: '12px' }}>
                  Global Change:
                  <br />
                  Applied to all {isFinance ? formatName(financeType) : ''}{' '}
                  pencils
                </span>
              }
              placement="left"
              arrow
            >
              <Public fontSize="medium" />
            </Tooltip>
          </div>
        </div>
      ) : (
        <div>{formatName(change.name)}</div>
      )}
      <ul className="offer-prop-change-detail">
        {filteredListItemChanges?.map(itemChange => renderDetail(itemChange))}
      </ul>
    </div>
  );
};

const getTextChanges = (
  propName: string,
  itemChange: ListItemChangeDetail,
): ChangeInfo[] => {
  if (
    itemChange.type === ListItemChangeType.ADD ||
    itemChange.type === ListItemChangeType.DELETE
  ) {
    if (EXCLUDE_FROM_ADD_DELETE.includes(itemChange.name)) {
      return [];
    }
    const value = formatValue({
      name: itemChange.changes?.[0].name ?? itemChange.name,
      value: itemChange.changes?.[0].value,
      parent: propName,
    });
    const changeInfo = {
      changeType:
        itemChange.type === ListItemChangeType.ADD
          ? ChangeType.ADD
          : ChangeType.DELETE,
      name: itemChange.name,
      values: [value],
    };
    return [changeInfo];
  }
  if (itemChange.type === ListItemChangeType.EDIT) {
    if (itemChange.changes && itemChange.changes.length > 0) {
      // handle selectedProducts differently
      if (propName === SELECTED_PRODUCTS) {
        const retailPriceChange = itemChange.changes.find(
          changeDetail => changeDetail.name === 'retailPrice',
        );
        if (retailPriceChange) {
          const value = formatValue({
            name: 'retailPrice',
            value: retailPriceChange.value,
          });
          const changeInfo = {
            changeType: ChangeType.EDIT,
            name: itemChange.name,
            values: [value],
          };
          return [changeInfo];
        }
        return [];
      }
      if (EXCLUDE_FROM_EDIT.includes(propName)) {
        return [];
      }
      return itemChange.changes
        .filter(c => {
          const oldValue = formatValue({
            name: c.name,
            value: c.oldValue,
            parent: propName,
          });
          const newValue = formatValue({
            name: c.name,
            value: c.value,
            parent: propName,
          });
          return (
            !EXCLUDE_ITEM_PROPERTIES.includes(c.name) &&
            newValue !== 'none' &&
            oldValue !== newValue
          );
        })
        .map(c => ({
          changeType: ChangeType.CHANGE,
          name: c.name,
          values: [
            formatValue({ name: c.name, value: c.oldValue, parent: propName }),
            formatValue({ name: c.name, value: c.value, parent: propName }),
          ],
        }));
    }
  }
  return [];
};

export const isListChangeEmpty = (change: ListChange): boolean => {
  const filteredListItemChanges = change.listItemChanges?.filter(
    itemChange => getTextChanges(change.name, itemChange).length > 0,
  );
  return !filteredListItemChanges || filteredListItemChanges.length === 0;
};
